import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../../components/Header";
import authApi from "../../../../api/authApi"; // Make sure this contains verifyResetToken and updatePassword

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const isButtonEnabled = newPassword && confirmPassword;

    useEffect(() => {
        if (!token) {
            setError("Invalid reset link.");
            return;
        }

        const verifyToken = async () => {
            try {
                await authApi.verifyResetToken(token);
            } catch {
                setError("Reset link is invalid or has expired.");
            }
        };

        verifyToken();
    }, [token]);

    const getPasswordStrength = (password: string) => {
        if (password.length >= 8) return { text: "Excellent", color: "#34A853", width: "108px" };
        if (password.length >= 6) return { text: "Strong", color: "#4285F4", width: "81px" };
        if (password.length >= 4) return { text: "Average", color: "#F5BF4F", width: "47px" };
        if (password.length >= 2) return { text: "Weak", color: "#EB4335", width: "18px" };
        return null;
    };

    const passwordStrength = getPasswordStrength(newPassword);

    const handleSetPassword = async () => {
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (!token) {
            setError("Missing or invalid token");
            return;
        }

        const email = localStorage.getItem("email");

        if (!email) {
            setError("Missing email. Please start from the forgot password page.");
            return;
        }
        const new_password = newPassword

        try {
            setLoading(true);
            await authApi.updatePassword(email, new_password, token);
            navigate("/");
        } catch {
            setError("Failed to reset password. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />
            <div className="flex flex-col items-center flex-1 bg-gray-50 p-5">
                <div className="bg-white sm:w-2/5 max-w-xl py-10 px-8 rounded-lg shadow-sm">
                    <h1 className="text-2xl font-bold text-gray-800 text-center mb-2">Reset Password</h1>
                    <p className="text-center text-base font-medium text-gray-800 mb-8">
                        Kindly set your new password.
                    </p>

                    {/* New Password */}
                    <div className="w-full mb-4">
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                            New Password
                        </label>
                        <input
                            id="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full h-12 px-4 rounded-md border bg-gray-100 focus:ring-2 focus:ring-blue-500"
                        />
                        {passwordStrength && (
                            <div className="flex items-center gap-2 mt-2">
                                <span className="text-sm" style={{ color: passwordStrength.color }}>
                                    {passwordStrength.text}
                                </span>
                                <div className="w-[108px] h-[4px] rounded bg-gray-300 relative">
                                    <div
                                        className="absolute left-0 top-0 h-full rounded"
                                        style={{ width: passwordStrength.width, backgroundColor: passwordStrength.color }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div className="w-full mb-4">
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Password
                        </label>
                        <input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full h-12 px-4 rounded-md border bg-gray-100 focus:ring-2 focus:ring-blue-500"
                        />
                        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
                    </div>

                    {/* Submit Button */}
                    <button
                        onClick={handleSetPassword}
                        disabled={!isButtonEnabled || loading}
                        className={`w-full button-1 ${isButtonEnabled ? "bg-gray-800 hover:bg-gray-700" : "!bg-gray-400 !cursor-not-allowed"}`}
                    >
                        {loading ? "Submitting..." : "Set Password"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
